import type { BillingBlock } from "@/hooks/usePermissions";
import { fill } from "@/lib/i18n/fill";
import type { Translations } from "@/lib/i18n/types";
import { BillingRequestError, type BillingErrorCode } from "./repository";
import type { BillingDetailsInput } from "./types";
import { formatStorageBytes } from "@/domains/documents/services/buildingStorageQuota";

/**
 * Why a control the user DOES hold is unavailable right now, in their language.
 *
 * `usePermissions` deals in machine codes because it is called from hundreds of
 * places, many with no language provider; every screen that keeps a withheld
 * control visible translates the code here rather than inventing its own wording.
 */
export function billingBlockMessage(
  block: BillingBlock | null,
  t: Translations
): string | undefined {
  if (!block) return undefined;
  const byBlock: Record<BillingBlock, string> = {
    read_only: t.billing.shell.reasonReadOnly,
    status_unknown: t.billing.shell.reasonStatusUnknown,
    details_missing: t.billing.shell.reasonDetailsMissing,
    payment_method_missing: t.billing.shell.reasonPaymentMethodMissing,
    activation_pending: t.billing.plans.activationPending,
    plan_selection_required: t.billing.plans.ownerCreation,
  };
  return byBlock[block];
}

/**
 * Turn a failed billing mutation into something a user can read.
 *
 * The `billing` edge function's `message` is English prose written for a log, so
 * it never reaches the screen: a recognised `code` picks a translated sentence
 * and everything else falls back to the caller's own wording.
 */
/**
 * The database's own billing refusals, which reach the app as PostgREST errors
 * rather than as an edge-function code.
 *
 * `private.billing_writable_guard` refuses an INSERT into `buildings`,
 * `building_projects` or `documents` with `ERRCODE 42501` and one of three
 * sentences. All three are actionable — settle the invoice, save the details,
 * restore the building — so the user has to be told which one applied instead of
 * "upload failed". 42501 alone cannot say: an ordinary row-level-security denial
 * carries the same code, so the raise's own wording is the only discriminator
 * the guard offers. The needles are the fragments of those sentences that carry
 * the meaning; keep them in step with the migration if it is ever reworded.
 */
const BILLING_GUARD_NEEDLES = [
  { needle: "cannot add buildings", reason: "details_missing" },
  { needle: "read only until its overdue invoice", reason: "read_only" },
  { needle: "past the date it stopped being usable", reason: "building_lapsed" },
] as const;

export type BillingGuardReason = (typeof BILLING_GUARD_NEEDLES)[number]["reason"];

function errorText(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  const message = (error as { message?: unknown })?.message;
  return typeof message === "string" ? message : "";
}

/** Which write guard refused this, or null when it was not one of ours. */
export function billingGuardReason(error: unknown): BillingGuardReason | null {
  const code = (error as { code?: unknown })?.code;
  // A raise from the guard is always 42501; anything carrying a different
  // Postgres code is a different failure that happens to quote us.
  if (typeof code === "string" && code !== "42501") return null;
  const text = errorText(error).toLowerCase();
  if (!text) return null;
  const hit = BILLING_GUARD_NEEDLES.find((entry) => text.includes(entry.needle));
  return hit ? hit.reason : null;
}

/**
 * The localised sentence for a database write guard, or undefined when the
 * failure was not one — so a caller can keep its own error handling for
 * everything else.
 */
export function billingGuardMessage(error: unknown, t: Translations): string | undefined {
  const planRefusal = buildingPlanLimitMessage(error, t);
  if (planRefusal) return planRefusal;
  if (errorText(error).includes("BILLING_PLAN_ACTIVATION_PENDING"))
    return t.billing.plans.activationPending;
  if (errorText(error).includes("BILLING_PLAN_SELECTION_REQUIRED"))
    return t.billing.plans.ownerCreation;
  const reason = billingGuardReason(error);
  if (!reason) return undefined;
  const byReason: Record<BillingGuardReason, string> = {
    read_only: t.billing.guards.readOnly,
    details_missing: t.billing.guards.detailsMissing,
    building_lapsed: t.billing.guards.buildingLapsed,
  };
  return byReason[reason];
}

const PLAN_LIMITS = [
  {
    code: "QB001",
    marker: "BILLING_FREE_BUILDING_LIMIT_EXCEEDED",
    key: "freeBuildingLimit",
    fallback: 1,
  },
  { code: "QB002", marker: "BILLING_PROJECT_LIMIT_EXCEEDED", key: "projectLimit", fallback: 1 },
  { code: "QB003", marker: "BILLING_PM_ISSUE_LIMIT_EXCEEDED", key: "pmIssueLimit", fallback: 20 },
  { code: "QB004", marker: "BILLING_CHAT_LIMIT_EXCEEDED", key: "chatLimit", fallback: 10 },
  {
    code: "QB005",
    marker: "BILLING_FREE_ACCOUNT_LIMIT_EXCEEDED",
    key: "freeAccountLimit",
    fallback: 1,
  },
] as const;

export function buildingPlanLimitMessage(error: unknown, t: Translations): string | undefined {
  const code = (error as { code?: unknown })?.code;
  const text = errorText(error);
  const rule = PLAN_LIMITS.find((entry) => code === entry.code || text.includes(entry.marker));
  if (!rule) return undefined;
  const raw = (error as { details?: unknown })?.details;
  let details: unknown = raw;
  if (typeof raw === "string") {
    try {
      details = JSON.parse(raw);
    } catch {
      details = null;
    }
  }
  const limit = (details as { limit?: unknown })?.limit;
  const allowance =
    typeof limit === "number" && Number.isSafeInteger(limit) && limit >= 0 ? limit : rule.fallback;
  return fill(t.billing.plans[rule.key], { limit: String(allowance) });
}

/**
 * The two codes whose server message is the whole point of the refusal.
 *
 * Every other code names a STATE the app already has a translated sentence for,
 * so the English prose stays in the console. These two name a specific value —
 * which field is wrong, or what Stripe said about the VAT number it would not
 * take — and a code cannot carry that. Showing "billing is temporarily
 * unavailable" for `Invalid value for eu_vat` is the failure this exists to
 * stop: the customer is told to try again later for something only they can fix.
 *
 * The message is the server's, in English, deliberately: a wrong sentence in the
 * right language is worse than a right one in the wrong language, and the
 * surrounding sentence is localised. The client's own per-country VAT rule
 * (`vat.ts`) is what keeps this path rare.
 */
const CODES_WITH_SERVER_DETAIL: ReadonlySet<BillingErrorCode> = new Set(["validation", "stripe"]);

/**
 * The Edge Function's own prefix on a Stripe failure it could explain.
 *
 * `errorResponse` answers `Billing request failed: <what Stripe said>`, and the
 * app frames it again in the active language — so the prefix is stripped rather
 * than printed twice, once in English and once in Estonian.
 */
const SERVER_STRIPE_PREFIX = "Billing request failed";

/** Server prose worth showing: present, short enough to read, not the generic. */
function serverDetail(error: BillingRequestError): string | null {
  const message = error.message.trim();
  if (message === SERVER_STRIPE_PREFIX) return null;
  const detail = message.startsWith(`${SERVER_STRIPE_PREFIX}: `)
    ? message.slice(SERVER_STRIPE_PREFIX.length + 2).trim()
    : message;
  if (!detail) return null;
  return detail.length > 300 ? null : detail;
}

function planStorageAllowanceMessage(
  error: BillingRequestError,
  t: Translations
): string | undefined {
  if (error.code !== "plan_storage_limit") return undefined;
  const limit = (error.details as { limit_bytes?: unknown })?.limit_bytes;
  if (typeof limit !== "number" || !Number.isSafeInteger(limit) || limit < 0) return undefined;
  return fill(t.billing.plans.planStorageAllowance, { limit: formatStorageBytes(limit) });
}

function billingRequestErrorMessage(
  error: BillingRequestError,
  code: BillingErrorCode,
  t: Translations
): string {
  const byCode: Record<BillingErrorCode, string> = {
    not_owner: t.billing.errors.notOwner,
    read_only: t.billing.errors.readOnly,
    details_missing: t.billing.errors.detailsMissing,
    payment_method_missing: t.billing.errors.paymentMethodMissing,
    pilot: t.billing.errors.pilot,
    validation: t.billing.errors.validation,
    stripe: t.billing.errors.stripe,
    plan_limit: t.billing.plans.planLimit,
    free_building_limit: t.billing.plans.freeBuildingLimit,
    free_account_limit: t.billing.plans.freeAccountLimit,
    plan_selection_required: t.billing.plans.ownerCreation,
    plan_storage_limit: t.billing.plans.planStorageLimit,
    plan_change_busy: t.billing.plans.planChangeBusy,
  };
  const allowance = planStorageAllowanceMessage(error, t);
  if (allowance) return allowance;
  const detail = CODES_WITH_SERVER_DETAIL.has(code) ? serverDetail(error) : null;
  if (!detail) return byCode[code];
  return fill(
    code === "validation" ? t.billing.errors.validationDetail : t.billing.errors.stripeDetail,
    { message: detail }
  );
}

export function billingErrorMessage(error: unknown, t: Translations, fallback: string): string {
  const planRefusal = buildingPlanLimitMessage(error, t);
  if (planRefusal) return planRefusal;
  if (error instanceof BillingRequestError && error.code)
    return billingRequestErrorMessage(error, error.code, t);
  return fallback;
}

/**
 * The details form's field a server refusal belongs under.
 *
 * `field` is what the Edge Function names when it can; the needles are the
 * fallback for a refusal that names the field in prose alone — a Stripe tax-id
 * rejection quotes the VAT number, and PostgreSQL's own CHECK-constraint message
 * names the column. Anything unrecognised returns null and stays a toast, which
 * is the honest answer: an error pinned to the wrong input reads as that input
 * being wrong.
 */
const FIELD_BY_SERVER_NAME: Readonly<Record<string, keyof BillingDetailsInput>> = {
  company_name: "companyName",
  registry_code: "registryCode",
  vat_number: "vatNumber",
  legal_address: "legalAddress",
  invoice_email: "invoiceEmail",
  purchase_order_number: "purchaseOrderNumber",
  payment_method: "paymentMethod",
};

const FIELD_NEEDLES: ReadonlyArray<[needle: string, field: keyof BillingDetailsInput]> = [
  ["eu_vat", "vatNumber"],
  ["vat number", "vatNumber"],
  ["vat_number", "vatNumber"],
  ["registry code", "registryCode"],
  ["registry_code", "registryCode"],
  ["company name", "companyName"],
  ["legal address", "legalAddress"],
  ["address", "legalAddress"],
  ["invoice email", "invoiceEmail"],
  ["purchase order", "purchaseOrderNumber"],
  ["payment method", "paymentMethod"],
];

export function billingErrorField(error: unknown): keyof BillingDetailsInput | null {
  if (!(error instanceof BillingRequestError)) return null;
  const named = error.field ? FIELD_BY_SERVER_NAME[error.field] : undefined;
  if (named) return named;
  const text = error.message.toLowerCase();
  return FIELD_NEEDLES.find(([needle]) => text.includes(needle))?.[1] ?? null;
}
